import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const TermsOfUse = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = 'June 2025';

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Terms of Use</h1>
          <p className="text-gray-500">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="space-y-10 text-gray-700 leading-relaxed">

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using the Spire Group Inc. website (the &ldquo;Site&rdquo;), you accept and agree to be bound by these Terms of Use and our <Link to="/privacy" className="text-spire-purple hover:underline">Privacy Policy</Link>. If you do not agree to these terms, please do not use this Site.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. About Spire Group Inc.</h2>
              <p>
                Spire Group Inc. is a healthcare real estate consulting firm based in Calgary, Alberta, Canada. We assist healthcare professionals across North America with site selection, lease negotiation, and real estate advisory services. The information provided on this Site is for general informational purposes only and does not constitute legal, financial, or real estate advice.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Use of the Site</h2>
              <p>You agree to use this Site only for lawful purposes and in a manner that does not infringe the rights of others. You must not:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Use the Site to transmit any unsolicited or unauthorized advertising or promotional material</li>
                <li>Attempt to gain unauthorized access to any part of the Site or its related systems</li>
                <li>Use automated tools, scrapers, or bots to access, copy, or monitor any portion of the Site</li>
                <li>Transmit any material that is defamatory, offensive, or otherwise objectionable</li>
                <li>Impersonate Spire Group Inc. or any of its employees or representatives</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Intellectual Property</h2>
              <p>
                All content on this Site, including text, graphics, logos, images, and software, is the property of Spire Group Inc. and is protected by Canadian and international copyright laws. You may not reproduce, distribute, modify, or create derivative works without our prior written consent.
              </p>
              <p className="mt-3">
                The Spire Group name, logo, and related marks are trademarks of Spire Group Inc. Nothing on this Site grants any licence to use our trademarks.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. No Professional Advice</h2>
              <p>
                The content on this Site is provided for general informational purposes only. It is not intended to constitute professional real estate, legal, financial, or tax advice. Engaging Spire Group Inc. for consulting services is subject to a separate agreement between the parties. Always consult qualified professionals before making real estate decisions.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Disclaimer of Warranties</h2>
              <p>
                This Site and its content are provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without any warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. Spire Group Inc. does not warrant that the Site will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by applicable law, Spire Group Inc. and its directors, officers, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, this Site or its content, even if we have been advised of the possibility of such damages.
              </p>
              <p className="mt-3">
                Our total liability to you for any claims arising from your use of this Site shall not exceed one hundred Canadian dollars (CAD $100).
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Links</h2>
              <p>
                This Site may contain links to third-party websites for your convenience. Spire Group Inc. does not endorse or control those sites and is not responsible for their content, privacy practices, or terms. Accessing third-party sites is at your own risk.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Governing Law</h2>
              <p>
                These Terms of Use are governed by and construed in accordance with the laws of the Province of Alberta and the federal laws of Canada applicable therein, without regard to conflict of law principles. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts of Alberta.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms of Use at any time. Changes will be effective immediately upon posting to the Site. Your continued use of the Site after changes are posted constitutes your acceptance of the revised terms. We encourage you to review these terms periodically.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
              <p>For questions about these Terms of Use, please contact:</p>
              <div className="mt-4 p-6 bg-gray-50 rounded-2xl">
                <p className="font-bold text-gray-900">Spire Group Inc.</p>
                <p>Calgary, Alberta, Canada</p>
                <p>Email: <a href="mailto:info@spiregrp.com" className="text-spire-purple hover:underline">info@spiregrp.com</a></p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Back link */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 flex gap-6">
          <Link to="/" className="text-spire-purple hover:underline font-semibold">&larr; Back to Home</Link>
          <Link to="/privacy" className="text-spire-purple hover:underline font-semibold">Privacy Policy &rarr;</Link>
        </div>
      </section>
    </div>
  );
};

export default TermsOfUse;
